import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { TemplateAspectPresetId } from "../../Config/templateAspectRatios";

export type BatchTemplateIDs = {
  landcsapeTemp?: string;
  portraitTemp?: string;
};

/**
 * Mirrors Image Properties “Enable tiled watermark” + pattern + sliders while creating a template.
 */
export type CreateTemplateTilePattern1Draft = {
  tiledEnabled: boolean;
  /** TilePatternRow id, e.g. "pattern1" | "none" */
  tilePatternId: string | null;
  size: number;
  spacing: number;
  opacity: number;
  /** Degrees, -180…180 */
  rotation: number;
};

export const defaultCreateTemplateTilePattern1: CreateTemplateTilePattern1Draft = {
  tiledEnabled: false,
  tilePatternId: null,
  size: 0.1987805,
  spacing: 0.2378049,
  opacity: 1,
  rotation: 0,
};

interface UserState {
  user_Auth: Record<string, unknown>;
  user_Verify: Record<string, unknown>;
  template: Record<string, unknown>;
  current_TemplateID: string | null | BatchTemplateIDs;
  template_Edit: boolean;
  edit_Template: boolean;
  create_Template: boolean;
  templateChanged: boolean;
  batchDownload: boolean;
  batchImages: unknown;
  reviews: unknown[];
  readyToDownload: boolean;
  online: boolean;
  discarded: boolean;
  canvasSize: { width: number; height: number } | null;
  imageFrame: { width: number; height: number; offsetX?: number; offsetY?: number } | null;
  createTemplateTilePattern1: CreateTemplateTilePattern1Draft;
  /**
   * Aspect ratio while creating or updating a template. We remap to `"1:1"` right
   * before save so persisted watermark data matches the legacy square flow.
   */
  templateAuthoringAspectPreset: TemplateAspectPresetId;
  /**
   * EWW-409/430/435. True only while Header's "Save as Template" is saving an ALREADY-EDITED
   * single image, as opposed to authoring a brand new template from scratch (/addtemplate).
   *
   * `create_Template` is reused for both flows, and canvas.tsx normally treats `create_Template`
   * as "enter authoring mode": resize the canvas to make room for the aspect-ratio bar, and
   * switch every watermark's position/size reference from the photo's own frame to the full
   * (resized) canvas. That's correct for a brand new template, where there is no existing photo
   * layout to preserve. It is wrong here: the user already positioned watermarks against the
   * CURRENT canvas, and resizing out from under them mid-save is what shifted their saved
   * position (worse the further from centre) and, separately, left the canvas visibly blank
   * when we later tried to keep the user on it instead of navigating to /home.
   *
   * The iOS app avoids this entirely: `CanvasScreenViewController.initiateSaveTempleteInfo`
   * (its equivalent of this save) deliberately leaves `eCanvasMode` at `.eSingle` -- the
   * `// eCanvasMode = .eCreateTemplate` line is commented out -- and instead sets
   * `currentCanvasRatio` to a `.custom` ratio matching the CURRENT, unchanged canvas view size.
   * This flag reproduces that: canvas.tsx and Header.tsx both check it to skip the resize/
   * reference-frame switch for this one flow, while every other `create_Template` consumer
   * (button visibility, `ensureSquareTemplateSave`, the API payload) is untouched.
   */
  savingExistingImageAsTemplate: boolean;
}

const initState: UserState = {
  user_Auth: {},
  user_Verify: {},
  template: {},
  current_TemplateID: null,
  // loader: false,
  template_Edit: false,
  edit_Template: false,
  create_Template: false,
  templateChanged: false,
  batchDownload: false,
  batchImages: false,
  reviews: [],
  readyToDownload: false,
  online: true,
  discarded: false,
  canvasSize: null,
  // Stores the rendered image frame on canvas for flexible_frame calculation
  // { width, height, offsetX, offsetY }
  imageFrame: null,
  createTemplateTilePattern1: { ...defaultCreateTemplateTilePattern1 },
  templateAuthoringAspectPreset: "1:1",
  savingExistingImageAsTemplate: false,
};
export const userSlice = createSlice({
  name: "userSlice",
  initialState: initState,
  reducers: {
    setUserAuth: (state, action: PayloadAction<Record<string, unknown>>) => {
      state.user_Auth = action.payload;
    },
    setUserVerify: (state, action: PayloadAction<Record<string, unknown>>) => {
      state.user_Verify = action.payload;
      (state as UserState & { loader?: boolean }).loader = false;
    },
    setTemplate: (state, action: PayloadAction<Record<string, unknown>>) => {
      state.template = action.payload;
    },
    setID: (state, action: PayloadAction<string | null | BatchTemplateIDs>) => {
      state.current_TemplateID = action.payload;
    },
    setBatchImages: (state, action: PayloadAction<unknown>) => {
      state.batchImages = action.payload;
    },
    setCanvasSize: (state, action: PayloadAction<{ width: number; height: number } | null>) => {
      state.canvasSize = action.payload;
    },
    setImageFrame: (state, action: PayloadAction<{ width: number; height: number; offsetX?: number; offsetY?: number } | null>) => {
      state.imageFrame = action.payload;
    },
    setReviews: (state, action: PayloadAction<unknown[]>) => {
      state.reviews = action.payload;
    },
    setTemplate_Edit: (state, action: PayloadAction<boolean>) => {
      state.template_Edit = action.payload;
    },
    setEdit_Template: (state, action: PayloadAction<boolean>) => {
      state.edit_Template = action.payload;
      state.create_Template = false;
      // Edit mode always uses the stored (square) canvas from the saved template;
      // keep the preset at 1:1 so the ratio bar default matches.
      state.templateAuthoringAspectPreset = "1:1";
    },
    setCreate_Template: (state, action: PayloadAction<boolean>) => {
      state.create_Template = action.payload;
      state.edit_Template = false;
      // Reset ratio preset whenever the create flow toggles, so each new
      // authoring session starts from 1:1 and never inherits a previous ratio.
      state.templateAuthoringAspectPreset = "1:1";
    },
    setTemplateAuthoringAspectPreset: (
      state,
      action: PayloadAction<TemplateAspectPresetId>
    ) => {
      state.templateAuthoringAspectPreset = action.payload;
    },
    setSavingExistingImageAsTemplate: (state, action: PayloadAction<boolean>) => {
      state.savingExistingImageAsTemplate = action.payload;
    },
    setCreateTemplateTilePattern1Draft: (
      state,
      action: PayloadAction<Partial<CreateTemplateTilePattern1Draft>>
    ) => {
      state.createTemplateTilePattern1 = {
        ...state.createTemplateTilePattern1,
        ...action.payload,
      };
    },
    setTemplate_Changed: (state, action: PayloadAction<boolean>) => {
      state.templateChanged = action.payload;
    },
    setOnline: (state, action: PayloadAction<boolean>) => {
      state.online = action.payload;
    },
    setBatchDownload: (state, action: PayloadAction<boolean>) => {
      state.batchDownload = action.payload;
      state.readyToDownload = !action.payload;
    },
    setDiscarded: (state, action: PayloadAction<boolean>) => {
      state.discarded = action.payload;
    },
    ClearUser: () => {
      return { ...initState };
    },
    ClearCache: (state) => {
      return {
        ...state,
        template_Edit: false,
        edit_Template: false,
        create_Template: false,
        templateChanged: false,
        batchDownload: false,
        createTemplateTilePattern1: { ...defaultCreateTemplateTilePattern1 },
        templateAuthoringAspectPreset: "1:1",
        savingExistingImageAsTemplate: false,
      };
    },
  },
  extraReducers: (builder) => {
    // "Enable tiled watermark" lives here as ONE global draft rather than per-watermark, so
    // nothing about clearing the canvas naturally clears it. That let a tile watermark from a
    // finished session stay switched on and silently auto-tile the next plain text/image
    // watermark the user added -- including across flows (single image -> create template).
    //
    // `WaterMarks/deleteAll` is the one signal every fresh-session entry point already
    // dispatches (new template, new single image, /home, batch page, template switching), so
    // resetting here covers them all instead of patching each call site. Safe for the template
    // paths too: loading a template always dispatches its own
    // `setCreateTemplateTilePattern1Draft` from the template's stored metadata right after
    // clearing, so a template's real tile settings still win.
    builder.addMatcher(
      (action: { type: string }) => action.type === "WaterMarks/deleteAll",
      (state) => {
        state.createTemplateTilePattern1 = { ...defaultCreateTemplateTilePattern1 };
      }
    );
  },
});

// Action creators are generated for each case reducer function
export const {
  setUserAuth,
  setUserVerify,
  setTemplate,
  setID,
  setBatchImages,
  setTemplate_Edit,
  setTemplate_Changed,
  setEdit_Template,
  setCreate_Template,
  setCreateTemplateTilePattern1Draft,
  setBatchDownload,
  ClearUser,
  setReviews,
  ClearCache,
  setOnline,
  setCanvasSize,
  setDiscarded,
  setImageFrame,
  setTemplateAuthoringAspectPreset,
  setSavingExistingImageAsTemplate,
} = userSlice.actions;

export default userSlice.reducer;
